import moment from 'moment';
import { firstLetterUp, isWeb } from '@helpers';

interface TransformToODataParams {
  operator: string;
  field: string;
  value: any;
}

export const transformToOData = ({ operator, field, value }: TransformToODataParams): string | null | undefined => {
  if (value == undefined) {
    return null;
  }
  const capitalizedField = firstLetterUp(field);
  const specialCases = ['false', 'true'];
  const dateFormats = [moment.ISO_8601, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ'];
  const escapeODataString = (v: string) => String(v).replace(/'/g, "''");
  const escapeODataRegex = (v: string) => String(v).replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&').replace(/'/g, "''");
  const formatValue = (v: any) => {
    if (typeof v === 'boolean') {
      return v;
    }
    if (v instanceof Date) {
      return v.toISOString();
    }
    if (specialCases.includes(v)) {
      return v == 'true';
    }
    if (typeof v === 'string' && moment(v, dateFormats, true).isValid()) {
      return moment(v).toISOString();
    }
    if (Array.isArray(v)) {
      return v.map((item: string) => `'${escapeODataString(item)}'`);
    }
    if (typeof v === 'string') {
      return `'${escapeODataString(v)}'`;
    }
    return v;
  };

  const formattedValue = formatValue(value);

  switch (operator) {
    case 'contains':
      return `search.ismatch('/.*${escapeODataRegex(value)}.*/', '${capitalizedField}', 'full', 'any')`;
    case 'doesNotContain':
      return `not search.ismatch('/.*${escapeODataRegex(value)}.*/', '${capitalizedField}', 'full', 'any')`;
    case 'equals':
      return `${capitalizedField} eq ${formattedValue}`;
    case 'doesNotEqual':
      return `${capitalizedField} ne ${formattedValue}`;
    case 'startsWith':
      return `search.ismatch('${escapeODataString(value)}*', '${capitalizedField}')`;
    case 'endsWith':
      return `search.ismatch('/.*${escapeODataRegex(value)}/', '${capitalizedField}', 'full', 'any')`;
    case 'isEmpty':
      return `${capitalizedField} eq null`;
    case 'isNotEmpty':
      return `${capitalizedField} ne null`;
    case 'isAnyOf':
      if (!formattedValue?.length) {
        return;
      }
      if (formattedValue?.length === 1) {
        return `${capitalizedField} eq ${formattedValue}`;
      }
      return `search.in(${capitalizedField}, '${value?.map(escapeODataString).join(',')}', ',')`;
    case 'is':
      if (value === '' || value === undefined) {
        return '';
      }
      if (formattedValue && moment(formattedValue).isValid()) {
        return `${capitalizedField} ge ${moment(formattedValue).subtract(1, 'days').toISOString()} and ${capitalizedField} lt ${moment(formattedValue)
          .add(1, 'days')
          .toISOString()} `;
      }
      return `${capitalizedField} eq ${formattedValue}`;
    case 'not':
      return `${capitalizedField} ne ${formattedValue}`;
    case 'after':
      return `${capitalizedField} gt ${formattedValue}`;
    case 'onOrAfter':
      return `${capitalizedField} ge ${formattedValue}`;
    case 'before':
      return `${capitalizedField} lt ${formattedValue}`;
    case 'onOrBefore':
      return `${capitalizedField} le ${formattedValue}`;
  }
};

export const getPersistedTableConfig = (tableName: string) => {
  if (!tableName || !isWeb) return null;
  try {
    const savedSort = window.localStorage.getItem(`${tableName}-sortModel`);
    const savedFilter = window.localStorage.getItem(`${tableName}-filterModel`);
    const savedVisibility = window.localStorage.getItem(`${tableName}-columnVisibility`);
    const savedODataFilter = window.localStorage.getItem(`${tableName}-oDataFilter`);

    const sortModel = savedSort ? JSON.parse(savedSort) : null;
    const filterModel = savedFilter ? JSON.parse(savedFilter) : null;
    const columnVisibilityModel = savedVisibility ? JSON.parse(savedVisibility) : null;
    const orderBy = sortModel?.length ? sortModel.map((s: any) => `${firstLetterUp(s.field)} ${s.sort}`) : null;

    let oDataFilter = savedODataFilter || null;
    if (!oDataFilter && filterModel?.items?.length) {
      const oDataFilterArr = filterModel.items.map(transformToOData).filter(Boolean);
      if (oDataFilterArr.length) {
        const logic = filterModel.logicOperator ?? 'and';
        oDataFilter = oDataFilterArr.length > 1 ? oDataFilterArr.join(` ${logic} `) : oDataFilterArr[0];
      }
    }

    return { sortModel, filterModel, columnVisibilityModel, oDataFilter, orderBy };
  } catch {
    return null;
  }
};

export const clearPersistedFilters = (tableName: string): void => {
  if (!tableName || !isWeb) return;
  try {
    window.localStorage.removeItem(`${tableName}-filterModel`);
    window.localStorage.removeItem(`${tableName}-oDataFilter`);
  } catch {}
};

export const clearPersistedSort = (tableName: string): void => {
  if (!tableName || !isWeb) return;
  try {
    window.localStorage.removeItem(`${tableName}-sortModel`);
  } catch {}
};
